// src/components/super-admin/SuperAdminSidebar.js

import { Box, Typography, useTheme, alpha } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { font } from '../../../styles/font';
import { useApplicationContext } from '../../../context/ApplicationContext';
import { Super_Admin_Module } from '../../../constant/ApplicationVariables';
import { 
    VpnKeyOutlined,
    ReceiptOutlined,
    DescriptionOutlined,
    ExtensionOutlined,
    Dashboard,
    Settings,
    People,
    Business
} from '@mui/icons-material';

// Custom Icons
import { OrganisationCustomIcon } from '../../styled/CustomIcons';

export default function SuperAdminSidebar() {
    const theme = useTheme();
    const {
        activeSuperAdminModule,
        setActiveSuperAdminModule
    } = useApplicationContext();

    /**
     * Menu items configuration
     */
    const menuItems = useMemo(() => [
        {
            label: "Licenses",
            value: Super_Admin_Module.license,
            icon: <VpnKeyOutlined />,
        },
        {
            label: "Organisations",
            value: Super_Admin_Module.organisation,
            icon: <OrganisationCustomIcon />,
        },
        {
            label: "Invoices",
            value: Super_Admin_Module.invoices,
            icon: <ReceiptOutlined />,
        },
        {
            label: "Standalone Licenses",
            value: Super_Admin_Module.standaloneLicense,
            icon: <DescriptionOutlined />,
        },
    ], []);

    /**
     * Handle menu item click
     */
    const handleClick = (moduleValue) => {
        setActiveSuperAdminModule(moduleValue);
    };

    /**
     * Set default active module on mount
     */
    useEffect(() => {
        if (!activeSuperAdminModule) {
            setActiveSuperAdminModule(Super_Admin_Module.license);
        }
    }, [activeSuperAdminModule, setActiveSuperAdminModule]);

    /**
     * Get theme colors for active state
     * Explicitly optimized to handle contrast inside dark mode
     */
    const activeColors = useMemo(() => {
        if (theme.palette.mode === 'light') {
            return {
                background: alpha(theme.palette.primary.main, 0.08),
                text: theme.palette.primary.main,
                icon: theme.palette.primary.main,
                border: theme.palette.primary.main,
                hover: alpha(theme.palette.primary.main, 0.04),
                dotShadow: alpha(theme.palette.primary.main, 0.3),
            };
        }
        
        // Dark Mode Adjustments: Force crisp text & icons on top of the deep green active pill
        return {
            background: alpha(theme.palette.primary.main, 0.7), 
            text: theme.palette.primary.contrastText || '#FFFFFF', // High-contrast text
            icon: theme.palette.primary.contrastText || '#FFFFFF', // High-contrast icon
            border: theme.palette.primary.light, 
            hover: alpha(theme.palette.primary.main, 0.9), 
            dotShadow: alpha(theme.palette.primary.light, 0.5),
        };
    }, [theme]);

    return (
        <Box className="flex flex-col gap-1 px-3 py-4 flex-1 overflow-y-auto">
            
            {/* Section Title */}
            <Typography 
                variant="caption" 
                sx={{ 
                    px: 1.5, 
                    pb: 1.5, 
                    fontWeight: 600, 
                    letterSpacing: 0.8,
                    color: theme.palette.text.secondary,
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                }}
            >
                Administration
            </Typography>

            {/* Menu Items */}
            {menuItems?.map((item, index) => {
                const isActive = activeSuperAdminModule === item?.value;
                
                return (
                    <Box
                        key={index}
                        onClick={() => handleClick(item?.value)}
                        className="flex items-center gap-2.5 px-3 py-2.5 cursor-pointer"
                        sx={{
                            borderRadius: 2,
                            transition: 'all 0.15s ease-in-out',
                            position: 'relative',
                            
                            // Active background styling
                            backgroundColor: isActive 
                                ? activeColors.background 
                                : 'transparent',
                            
                            // Text color transition rules
                            color: isActive 
                                ? activeColors.text 
                                : theme.palette.text.primary,
                            
                            // Left border line indicator
                            '&::before': isActive ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '20%',
                                height: '60%',
                                width: '3px',
                                backgroundColor: activeColors.border,
                                borderRadius: '0 3px 3px 0',
                                boxShadow: `0 0 12px ${activeColors.border}`,
                            } : {},
                            
                            // Hover state rules
                            '&:hover': {
                                backgroundColor: isActive 
                                    ? activeColors.hover 
                                    : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                            },
                            
                            opacity: item.disabled ? 0.4 : 1,
                            pointerEvents: item.disabled ? 'none' : 'auto',
                        }}
                    >
                        {/* Custom / MUI Icon Container */}
                        <Box 
                            className="flex items-center justify-center" 
                            sx={{ 
                                width: 28,
                                height: 28,
                                flexShrink: 0,
                                '& svg': {
                                    fontSize: '1.25rem',
                                    color: isActive 
                                        ? `${activeColors.icon} !important` 
                                        : `${theme.palette.text.secondary} !important`,
                                    transition: 'color 0.15s ease',
                                },
                                '&:hover svg': {
                                    color: isActive 
                                        ? `${activeColors.icon} !important` 
                                        : `${theme.palette.text.primary} !important`,
                                }
                            }}
                        >
                            {item?.icon}
                        </Box>

                        {/* Label Text */}
                        <Typography 
                            className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                            sx={{
                                fontSize: '0.875rem',
                                fontWeight: isActive ? 600 : 450,
                                // !important overrides any default MUI typography behaviors for active state
                                color: isActive 
                                    ? `${activeColors.text} !important` 
                                    : `${theme.palette.text.primary} !important`,
                                letterSpacing: '0.01em',
                                transition: 'color 0.15s ease',
                            }}
                        >
                            {item?.label}
                        </Typography>

                        {/* Active Indicator Dot */}
                        {isActive && (
                            <Box 
                                sx={{ 
                                    width: 7, 
                                    height: 7, 
                                    borderRadius: '50%', 
                                    backgroundColor: activeColors.border,
                                    flexShrink: 0,
                                    boxShadow: `0 0 12px ${activeColors.dotShadow}`,
                                }} 
                            />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}